export const BRAND = 'Restworld';

/**
 * Appends the brand suffix to a page title, exactly once.
 *
 * Call sites are not consistent: the job explorer and the job position page
 * pass a bare title, while `buildVenueMeta` already returns
 * `"<venue> | Restworld"` and its spec pins that shape. Appending
 * unconditionally would produce `"<venue> | Restworld | Restworld"`, which is
 * the defect this helper exists to prevent, so an already-branded title is
 * returned untouched.
 *
 * The brand is applied here rather than through next-seo's `titleTemplate`
 * because that library keeps the template in a module-level object: the first
 * page that renders with one makes every later page in the same server process
 * inherit it, which made the suffix depend on render order.
 */
export const withBrandSuffix = (title?: string | null): string => {
  const trimmed = title?.trim();
  if (!trimmed) return BRAND;
  if (trimmed === BRAND || trimmed.endsWith(` | ${BRAND}`)) return trimmed;

  // A few call sites wrote the brand as a prefix instead ("Restworld | Offerta
  // lavorativa"). Appending would double it, and returning it untouched would
  // leave the brand first while every other page has it last, so the prefix is
  // moved to the end. Only the exact "Restworld | " form counts: a title that
  // merely starts with the word, such as "Restworld è la piattaforma", is
  // ordinary copy and still gets the suffix.
  const prefix = `${BRAND} | `;
  if (trimmed.startsWith(prefix)) return `${trimmed.slice(prefix.length)} | ${BRAND}`;

  return `${trimmed} | ${BRAND}`;
};
